import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "transfer-details" }
const _hoisted_2 = {
  key: 0,
  class: "mb-1"
}
const _hoisted_3 = {
  key: 0,
  class: "mb-0 caption"
}
const _hoisted_4 = { class: "caption-strong" }
const _hoisted_5 = {
  key: 1,
  class: "mb-0 caption"
}
const _hoisted_6 = { class: "caption-strong" }
const _hoisted_7 = { key: 1 }
const _hoisted_8 = {
  key: 0,
  class: "mb-0 caption"
}
const _hoisted_9 = { class: "caption-strong" }
const _hoisted_10 = {
  key: 1,
  class: "mb-0 caption"
}
const _hoisted_11 = { class: "caption-strong" }

import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { TransferDetails } from '@/services/BookingService.interface'

  // Initialize i18n
  
export default /*@__PURE__*/_defineComponent({
  __name: 'TransferDetails',
  props: {
    tripInformation: {}
  },
  setup(__props: any) {

  const { t } = useI18n()

  // Define the props
  const props = __props

  // Funicular location mapping - specifically for Les Arcs destinations
  const funicularLocationMap: Record<string, string> = {
    // Les Arcs destinations by ID
    '96': 'Les Arcs 1600',
    '27': 'Les Arcs 1800', 
    '336': 'Les Arcs 1950',
    '97': 'Les Arcs 2000',
    // Common origin station
    'BOURG_SAINT_MAURICE': 'Bourg Saint Maurice',
    'BOURG SAINT MAURICE': 'Bourg Saint Maurice',
  }

  // Helper function to get display name for funicular locations
  const getDisplayName = (location: string | number, isDestination = false): string => {
    const locationStr = String(location).trim()
    
    // Only apply mapping for funicular transfers
    const details = transferDetails.value
    const isFunicular = details?.transferType === 'FUNICULAR' || 
                       (details?.outboundHours && !Array.isArray(details.outboundHours) && (details.outboundHours as any).provider === 'FUNICULAR') ||
                       (details?.inboundHours && !Array.isArray(details.inboundHours) && (details.inboundHours as any).provider === 'FUNICULAR')
    
    if (!isFunicular) {
      // For non-funicular transfers (bus/taxi), return original location
      return locationStr
    }
    
    // If this is a funicular destination and we have a destinationId, use that for mapping
    if (isDestination && details?.destinationId) {
      const destinationId = details.destinationId
      if (funicularLocationMap[destinationId]) {
        console.log('DEBUG: destinationId =', destinationId, '→', funicularLocationMap[destinationId])
        return funicularLocationMap[destinationId]
      }
    }
    
    // Otherwise use the location string for mapping (only for funicular)
    const result = funicularLocationMap[locationStr] || locationStr
    console.log('DEBUG: location =', locationStr, '→', result)
    return result
  }

  // Get the transfer details (handle array format)
  const transferDetails = computed((): TransferDetails | null => {
    if (Array.isArray(props.tripInformation) && props.tripInformation.length > 0) {
      // Check for the correct structure: tripInformation[0].details
      return props.tripInformation[0].details || props.tripInformation[0].tripInformation
    }
    return props.tripInformation
  })

  // Check if this is a taxi/funicular transfer (object format) or bus transfer (array format)
  const isTaxiTransfer = computed(() => {
    const details = transferDetails.value
    return details?.transferType === 'TAXI' || 
           details?.transferType === 'FUNICULAR' ||
           (details?.outboundHours && !Array.isArray(details.outboundHours) && (details.outboundHours as any).provider === 'FUNICULAR') ||
           (details?.inboundHours && !Array.isArray(details.inboundHours) && (details.inboundHours as any).provider === 'FUNICULAR') ||
           (details?.outboundHours && !Array.isArray(details.outboundHours))
  })


  // Get outbound transfer information
  const outboundTransferInfo = computed(() => {
    const details = transferDetails.value
    if (!details) return null

    if (isTaxiTransfer.value) {
      // Handle taxi/funicular transfers (object format) - format consistently with flights
      const outbound = details.outboundHours
      if (outbound && !Array.isArray(outbound) && 'from' in outbound && 'to' in outbound && details.origin && details.destination) {
        return {
          originLocation: getDisplayName(details.origin, false),
          destinationLocation: getDisplayName(details.destination, true),
          departureTime: outbound.from,
          arrivalTime: outbound.to,
          departureDate: (outbound as any).date || details.departureDate || (details as any).startDate || ''
        }
      }
    } else {
      // Handle bus transfers (array format)
      const selectedTransfer = getSelectedDepartureTransfer()
      if (selectedTransfer) {
        return formatBusTransferTime(selectedTransfer, false)
      }
    }
    
    return null
  })

  // Get return transfer information
  const returnTransferInfo = computed(() => {
    const details = transferDetails.value
    if (!details) return null

    if (isTaxiTransfer.value) {
      // Handle taxi/funicular transfers (object format) - format consistently with flights
      const inbound = details.inboundHours
      if (inbound && !Array.isArray(inbound) && 'from' in inbound && 'to' in inbound && details.origin && details.destination) {
        return {
          originLocation: getDisplayName(details.destination, true),
          destinationLocation: getDisplayName(details.origin, false),
          departureTime: inbound.from,
          arrivalTime: inbound.to,
          departureDate: (inbound as any).date || details.returnDate || (details as any).endDate || ''
        }
      }
    } else {
      // Handle bus transfers (array format)
      const selectedTransfer = getSelectedReturnTransfer()
      if (selectedTransfer) {
        return formatBusTransferTime(selectedTransfer, true)
      }
    }
    
    return null
  })

  // Find the selected departure transfer for bus transfers
  const getSelectedDepartureTransfer = () => {
    const details = transferDetails.value
    if (!details?.transferDepartureRef || !Array.isArray(details?.outboundHours)) {
      return null
    }
    
    return details.outboundHours.find(transfer => 
      transfer.trip_short_name === details.transferDepartureRef ||
      transfer.bus.toString() === details.transferDepartureRef
    )
  }

  // Find the selected return transfer for bus transfers
  const getSelectedReturnTransfer = () => {
    const details = transferDetails.value
    if (!details?.transferReturnRef || !Array.isArray(details?.inboundHours)) {
      return null
    }
    
    return details.inboundHours.find(transfer => 
      transfer.trip_short_name === details.transferReturnRef ||
      transfer.bus.toString() === details.transferReturnRef
    )
  }

  // Helper function to format date as DD/MM/YY
  const formatDate = (dateString: string): string => {
    if (!dateString) return ''
    const date = new Date(dateString)
    const day = date.getDate().toString().padStart(2, '0')
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const year = date.getFullYear().toString().slice(-2)
    return `${day}/${month}/${year}`
  }

  // Helper function to check if time indicates 24/7 service
  const is24HourService = (timeString: string): boolean => {
    return timeString === '00:01' || timeString === '23:59'
  }



  // Format bus transfer time with full route information
  const formatBusTransferTime = (transfer: any, isReturn: boolean) => {
    if (!transfer) return ''
    
    // Get departure and arrival information from the transfer itself
    const departLocation = transfer.departInfo?.name || ''
    const arriveLocation = transfer.arriveeInfo?.name || ''
    const departTime = transfer.depart
    const arriveTime = transfer.arrivee
    
    // If we have location info from the transfer, use it
    if (departLocation && arriveLocation && departTime && arriveTime) {
      return {
        originLocation: departLocation,
        destinationLocation: arriveLocation,
        departureTime: departTime,
        arrivalTime: arriveTime
      }
    }
    
    // Fallback: use main transfer route info and determine direction
    const details = transferDetails.value
    if (details?.origin && details?.destination && departTime && arriveTime) {
      if (isReturn) {
        // Return journey: destination → origin
        return {
          originLocation: details.destination,
          destinationLocation: details.origin,
          departureTime: departTime,
          arrivalTime: arriveTime
        }
      } else {
        // Outbound journey: origin → destination  
        return {
          originLocation: details.origin,
          destinationLocation: details.destination,
          departureTime: departTime,
          arrivalTime: arriveTime
        }
      }
    }
    
    // Final fallback with route type - return as string for legacy cases
    if (departTime && arriveTime) {
      const routeType = transfer.route_type || transfer.route?.type || 'Bus'
      return `${routeType} • ${departTime} → ${arriveTime}`
    } else if (departTime) {
      const routeType = transfer.route_type || transfer.route?.type || 'Bus'
      return `${routeType} • Departs ${departTime}`
    }
    
    return transfer.route_type || transfer.route?.type || 'Bus'
  }

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (outboundTransferInfo.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (typeof outboundTransferInfo.value === 'object')
            ? (_openBlock(), _createElementBlock("p", _hoisted_3, [
                _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(t)('reservations.outbound')), 1),
                _createTextVNode(" " + _toDisplayString(outboundTransferInfo.value.departureDate ? formatDate(outboundTransferInfo.value.departureDate) + ' ' : '') + _toDisplayString(outboundTransferInfo.value.originLocation) + _toDisplayString(!is24HourService(outboundTransferInfo.value.departureTime) ? ' ' + outboundTransferInfo.value.departureTime : '') + " → " + _toDisplayString(outboundTransferInfo.value.destinationLocation) + _toDisplayString(!is24HourService(outboundTransferInfo.value.arrivalTime) ? ' ' + outboundTransferInfo.value.arrivalTime : ''), 1)
              ]))
            : (_openBlock(), _createElementBlock("p", _hoisted_5, [
                _createElementVNode("span", _hoisted_6, _toDisplayString(_unref(t)('reservations.outbound')), 1),
                _createTextVNode(" " + _toDisplayString(outboundTransferInfo.value), 1)
              ]))
        ]))
      : _createCommentVNode("", true),
    (returnTransferInfo.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
          (typeof returnTransferInfo.value === 'object')
            ? (_openBlock(), _createElementBlock("p", _hoisted_8, [
                _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(t)('reservations.return')), 1),
                _createTextVNode(" " + _toDisplayString(returnTransferInfo.value.departureDate ? formatDate(returnTransferInfo.value.departureDate) + ' ' : '') + _toDisplayString(returnTransferInfo.value.originLocation) + _toDisplayString(!is24HourService(returnTransferInfo.value.departureTime) ? ' ' + returnTransferInfo.value.departureTime : '') + " → " + _toDisplayString(returnTransferInfo.value.destinationLocation) + _toDisplayString(!is24HourService(returnTransferInfo.value.arrivalTime) ? ' ' + returnTransferInfo.value.arrivalTime : ''), 1)
              ]))
            : (_openBlock(), _createElementBlock("p", _hoisted_10, [
                _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(t)('reservations.return')), 1),
                _createTextVNode(" " + _toDisplayString(returnTransferInfo.value), 1)
              ]))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})